
// src/services/RealTime/RealtimeWS.ts
import WSClient from './WSClient'
import { getAccessToken } from '@/libs/Auth'

let ws
let readyPromise
let refCount = 0

const listeners = {
  raw: new Set(),
  reconnected: new Set(),
  resubscribed: new Set()
}

function buildWsUrl () {
  const base = String(process.env.VUE_APP_PORTAL_WS_BASE || '').replace(/\/+$/, '')
  const stage = String(process.env.VUE_APP_PORTAL_WS_STAGE || '').replace(/^\/+|\/+$/g, '')

  if (!base) {
    throw new Error('Invalid URL for Portal WS API')
  }

  if (!stage) {
    return base
  }

  return `${base}/${stage}`
}

function getWsScope () {
  const explicit = String(process.env.VUE_APP_PORTAL_WS_SCOPE || '').trim()
  if (explicit) {
    return explicit
  }

  return 'api://5deddb7b-2801-42a0-a6f5-129b6bccbbcd/tickets-service'
}

function log (...args) {
  try {
    // eslint-disable-next-line no-console
    console.log('[Realtime][RealtimeWS]', ...args)
  } catch {
    // ignore
  }
}

function fanout (set, payload) {
  for (const fn of set) {
    try {
      fn(payload)
    } catch (e) {
      // ignore
    }
  }
}

async function ready () {
  if (readyPromise) {
    return readyPromise
  }

  readyPromise = (async () => {
    if (ws) {
      return
    }

    const url = buildWsUrl()
    const scope = getWsScope()

    ws = new WSClient({
      url,
      getToken: async () => {
        return (await getAccessToken({ scopes: [scope] }, true)) ?? ''
      }
    })

    if (typeof ws.setHandlers === 'function') {
      ws.setHandlers({
        onReconnected: (info) => {
          log('onReconnected', info)
          fanout(listeners.reconnected, info)
        },
        onResubscribed: (info) => {
          log('onResubscribed', info)
          fanout(listeners.resubscribed, info)
        },
        onRawMessage: (msg) => {
          fanout(listeners.raw, msg)
        }
      })
    }

    log('ws created')
  })()

  return readyPromise
}

export async function connect () {
  await ready()

  refCount += 1

  try {
    if (ws && typeof ws.connect === 'function') {
      await ws.connect()
    }
  } catch (e) {
    if (refCount > 0) {
      refCount -= 1
    }
    throw e
  }

  return ws
}

export function disconnect () {
  if (refCount > 0) {
    refCount -= 1
  }

  if (refCount > 0) {
    return
  }

  if (ws && typeof ws.disconnect === 'function') {
    ws.disconnect()
  }

  ws = null
  readyPromise = null
}

export async function getWs () {
  await ready()
  return ws
}

export function reset () {
  refCount = 0

  if (ws && typeof ws.disconnect === 'function') {
    try {
      ws.disconnect()
    } catch {
      // ignore
    }
  }

  ws = null
  readyPromise = null

  listeners.raw.clear()
  listeners.reconnected.clear()
  listeners.resubscribed.clear()
}

export function onRawMessage (fn) {
  listeners.raw.add(fn)
  return () => {
    listeners.raw.delete(fn)
  }
}

export function onReconnected (fn) {
  listeners.reconnected.add(fn)
  return () => {
    listeners.reconnected.delete(fn)
  }
}

export function onResubscribed (fn) {
  listeners.resubscribed.add(fn)
  return () => {
    listeners.resubscribed.delete(fn)
  }
}
